import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { TCommunicationEquipmentChange } from "../helpers/communication-types-helper";
import {
  buildCommunicationOriginalEmailSections,
  collectCommunicationOriginalEmailHighlights,
} from "../helpers/communication-original-email-helper";

interface ICommunicationOriginalEmailFieldProps {
  originalBody: string;
  equipmentChanges: TCommunicationEquipmentChange[];
  isExpanded?: boolean;
  maxHeightReductionPx?: number;
}

const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const renderHighlightedText = (text: string, highlightTerms: string[]): React.ReactNode => {
  if (!text) {
    return text;
  }

  if (!highlightTerms.length) {
    return text;
  }

  const pattern = new RegExp(`(${highlightTerms.map(escapeRegExp).join("|")})`, "giu");
  const matches = Array.from(text.matchAll(pattern));

  if (!matches.length) {
    return text;
  }

  const content: React.ReactNode[] = [];
  let currentIndex = 0;

  matches.forEach((match, index) => {
    const matchIndex = match.index ?? 0;
    const matchValue = match[0];

    if (matchIndex > currentIndex) {
      content.push(text.slice(currentIndex, matchIndex));
    }

    content.push(
      <mark key={`highlight-${matchIndex}-${index}`} className="communication-original-email__highlight">
        {matchValue}
      </mark>
    );

    currentIndex = matchIndex + matchValue.length;
  });

  if (currentIndex < text.length) {
    content.push(text.slice(currentIndex));
  }

  return content;
};

const CommunicationOriginalEmailField: React.FC<ICommunicationOriginalEmailFieldProps> = ({
  originalBody,
  equipmentChanges,
  isExpanded = false,
  maxHeightReductionPx = 0,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [maxHeight, setMaxHeight] = useState<number | undefined>(undefined);
  const sections = useMemo(
    () => buildCommunicationOriginalEmailSections(originalBody),
    [originalBody]
  );
  const highlightTerms = useMemo(
    () => collectCommunicationOriginalEmailHighlights(equipmentChanges),
    [equipmentChanges]
  );
  const syncMaxHeight = useCallback(() => {
    const container = containerRef.current;

    if (!container || typeof window === "undefined") {
      return;
    }

    const contentHeight = container.scrollHeight;

    if (!contentHeight) {
      return;
    }

    const desiredHeight = Math.ceil(contentHeight * 0.75);
    const minimumVisibleHeight = Math.min(contentHeight, 180);
    const viewportLimit = Math.min(360, Math.max(240, Math.floor(window.innerHeight * 0.36)));
    const nextMaxHeight = Math.min(
      contentHeight,
      Math.max(desiredHeight, minimumVisibleHeight),
      viewportLimit
    );
    const reducedMaxHeight = Math.max(
      Math.min(contentHeight, 96),
      nextMaxHeight - Math.max(0, maxHeightReductionPx)
    );

    setMaxHeight((currentHeight) =>
      currentHeight === reducedMaxHeight ? currentHeight : reducedMaxHeight
    );
  }, [maxHeightReductionPx]);

  useLayoutEffect(() => {
    if (!isExpanded) {
      return;
    }

    let frameId = 0;
    let timeoutId = 0;
    let resizeObserver: ResizeObserver | undefined;

    const scheduleSync = () => {
      if (typeof window === "undefined") {
        return;
      }

      if (frameId) {
        window.cancelAnimationFrame(frameId);
      }

      frameId = window.requestAnimationFrame(() => {
        syncMaxHeight();
      });
    };

    scheduleSync();
    timeoutId = window.setTimeout(syncMaxHeight, 260);

    if (typeof ResizeObserver !== "undefined" && containerRef.current) {
      resizeObserver = new ResizeObserver(() => {
        syncMaxHeight();
      });
      resizeObserver.observe(containerRef.current);
    }

    window.addEventListener("resize", syncMaxHeight);

    return () => {
      if (frameId) {
        window.cancelAnimationFrame(frameId);
      }

      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }

      resizeObserver?.disconnect();
      window.removeEventListener("resize", syncMaxHeight);
    };
  }, [highlightTerms, isExpanded, sections, syncMaxHeight]);

  if (!sections.length) {
    return (
      <div className="communication-original-email communication-original-email--empty">
        <span className="communication-original-email__empty">&mdash;</span>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="communication-original-email"
      style={maxHeight ? { maxHeight: `${maxHeight}px` } : undefined}
    >
      {sections.some((section) => section.type === "header") && (
        <div className="communication-original-email__headers">
          {sections
            .filter((section) => section.type === "header")
            .map((section) => (
              <div key={section.id} className="communication-original-email__header-row">
                <span className="communication-original-email__header-label">{section.label}:</span>
                <span className="communication-original-email__header-value">
                  {renderHighlightedText(section.text, highlightTerms)}
                </span>
              </div>
            ))}
        </div>
      )}

      <div className="communication-original-email__body">
        {sections
          .filter((section) => section.type !== "header")
          .map((section) => (
            section.type === "quote" ? (
              <div key={section.id} className="communication-original-email__quote">
                {renderHighlightedText(section.text, highlightTerms)}
              </div>
            ) : (
              <p key={section.id} className="communication-original-email__paragraph">
                {renderHighlightedText(section.text, highlightTerms)}
              </p>
            )
          ))}
      </div>
    </div>
  );
};

export default CommunicationOriginalEmailField;
